import { get } from 'lodash'
import { formatNumberWithCents } from '../converters/numberFormatter'
import { ALL_STATES_MAPPING } from '../constants/breadcrumbs'

const propertyTypePhraseMap = {
  single_family_home: `single family home`,
  condominium_unit: `condominium unit`,
  duplex_unit: 'duplex unit',
  multi_unit: `multi-unit building`,
}

export const mapAggregatePropertyPhrases = (data = {}) =>
  Object.keys(data).reduce((acc, propertyType) => {
    const propertyTypePrefix =
      data[propertyType] > 1 ? `${data[propertyType]} are` : `1 is a`
    if (data[propertyType] && propertyTypePhraseMap[propertyType]) {
      acc.push(
        `${propertyTypePrefix} ${propertyTypePhraseMap[propertyType]}${
          data[propertyType] > 1 ? 's' : ''
        }`
      )
    }
    return acc
  }, [])

export const mapAggregateSeoContent = (data = {}) =>
  Object.keys(data).reduce((acc, key) => {
    if (/^(?!du).+plex_unit$/.test(key)) {
      const multiUnitCount = acc.multi_unit || 0
      acc.multi_unit = multiUnitCount + data[key]
    } else {
      acc[key] = data[key]
    }
    return acc
  }, {})

export const getLocation = filters => {
  const locationInfo = []
  const state = get(filters, 'state[0]') || ''
  const county = get(filters, 'county[0]') || ''
  const city = get(filters, 'city[0]') || ''

  if (state) locationInfo.push(ALL_STATES_MAPPING[state.toUpperCase()])
  if (county) locationInfo.push(`${county} County, ${state.toUpperCase()}`)
  if (city) locationInfo.push(`${city}, ${state.toUpperCase()}`)

  return locationInfo.length ? locationInfo.pop() : null
}

export const getLowestStartingBid = assets => {
  const { list } = assets
  if (!list || !list.length) return 0
  let lowestStartingBid = list.reduce(
    (acc, listing) => Math.min(acc, listing?.auction?.starting_bid || acc),
    Infinity
  )
  if (lowestStartingBid === Infinity) lowestStartingBid = 0

  return lowestStartingBid
}

export const getRemoteBidContent = assets => {
  const { remoteBideTotals } = assets
  const propertyTypeContent = getPropertyTypeContent(assets)

  const remoteBidPostfix =
    remoteBideTotals?.true > 1
      ? 'of these properties are remote bid,'
      : 'of these properties is a remote bid,'

  if (!propertyTypeContent.length) {
    remoteBidPostfix.replace(/, ([^,]*)$/, '.')
  }

  return remoteBideTotals?.true
    ? `${remoteBideTotals?.true} ${remoteBidPostfix} `
    : ''
}

export const getPropertyTypeContent = assets => {
  const { propertyTypeTotals } = assets
  const propertySeoContent = mapAggregateSeoContent(propertyTypeTotals)
  return mapAggregatePropertyPhrases(propertySeoContent)
    .join(', ')
    .replace(/, ([^,]*)$/, ' and $1.')
}

export const generateQuestionAnswerContent = (filters, assets) => {
  const location = getLocation(filters)
  if (!location) return []
  const { total } = assets
  const lowestStartingBid = getLowestStartingBid(assets)
  const remoteBidSeoContent = getRemoteBidContent(assets)
  const propertyTypeContent = getPropertyTypeContent(assets)
  const currentDate = new Date(Date.now()).toLocaleString().split(',')[0]
  return [
    {
      question: `How many Foreclosures and Bank-Owned properties are in ${location}, currently?`,
      answer: `As of ${currentDate}, Auction.com has ${total} Foreclosure and Bank-Owned
                properties in ${location} with opening bids as low as ${formatNumberWithCents(
        lowestStartingBid
      )}. ${remoteBidSeoContent} ${propertyTypeContent}`,
    },
    {
      question:
        'Where can I learn how to invest in Foreclosure and Bank-Owned properties?',
      answer:
        "Foreclosure properties and Bank-Owned properties each have their own unique buying process. Learn more about investing in <a href='/lp/learn-more/foreclosure/'>Foreclosure home properties</a> here and learn how to buy <a href='/lp/learn-more/bank-owned/'> Bank-Owned properties here</a>. For more information on how to use Auction.com visit our <a href='/lp/learn-more/frequently-asked-questions/'>FAQ</a> section.",
    },
    {
      question: `How much does it cost to fix a Foreclosure house in ${location}?`,
      answer: `The <a href='/blog/tag/cost/'>cost</a> to fix a foreclosure property in ${location} can vary greatly depending on the condition of the property and the extent of renovations needed. Here are some factors to consider: <b>1) Home Inspection:</b> Before purchasing a Foreclosure, it is recommended to hire a home inspector for a thorough evaluation, if possible. Costs can vary, but it's worth the price if the inspector uncovers a major structural issue or another pricey problem uncovers a major structural issue or another pricey problem. <b>2) Home Repairs:</b> Foreclosures are likely to need some work. The list of repairs and renovations needed can be long. <b>3) Renovation Costs:</b> Home renovation and remodel costs can vary depending on the condition of the home and what remodel plans the buyer has.`,
    },
  ]
}

export const getFrequentlyAskedQuestions = (filters = {}, assets = {}) => {
  const questionAnswers = generateQuestionAnswerContent(filters, assets)
  return questionAnswers.length > 0
    ? questionAnswers.map(qa => ({
        question: qa.question,
        answer: qa.answer.replace(/(<([^>]+)>)/gi, ''),
      }))
    : []
}
